// ChannelTag — how a ticket arrived.
//
// Translated from prototype/primitives.jsx `ChannelTag`. The prototype's inline
// stroke SVGs are substituted with the existing MUI icon set per the handoff's
// "Assets" note; the mapping is one-for-one.
//
// ⚠️ NOT USED IN THE QUEUE. The handoff's queue design puts a channel icon on
// line 2 of the subject cell, but `TicketListRow` has no `channel` field and it
// cannot be derived (SearchTicketsRequest.channels only narrows the result set,
// it does not tell you a given row's channel). The queue therefore renders
// category alone on line 2. This atom is used where `channel` genuinely exists:
// the ticket detail header (`Ticket.channel`). Logged as a follow-up rather
// than worked around.
import React from 'react';
import { Box } from '@mui/material';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import PublicIcon from '@mui/icons-material/Public';
import SupportOutlinedIcon from '@mui/icons-material/SupportOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import TerminalIcon from '@mui/icons-material/Terminal';
import LinkIcon from '@mui/icons-material/Link';
import { SvgIconComponent } from '@mui/icons-material';
import { TicketChannel } from '../types';

interface ChannelMeta {
  label: string;
  Icon: SvgIconComponent;
}

const CHANNEL: Record<TicketChannel, ChannelMeta> = {
  web: { label: 'Web', Icon: DesktopWindowsOutlinedIcon },
  portal: { label: 'Portal', Icon: PublicIcon },
  widget: { label: 'Widget', Icon: SupportOutlinedIcon },
  email: { label: 'Email', Icon: MailOutlineIcon },
  api: { label: 'API', Icon: TerminalIcon },
  // The prototype labels `integration` as "Jira" because Jira is the only
  // connector built. Kept generic here — the connector catalog is per-tenant.
  integration: { label: 'Integration', Icon: LinkIcon }
};

interface ChannelTagProps {
  channel: TicketChannel;
  /** Icon only, with the label as a tooltip. */
  compact?: boolean;
}

export default function ChannelTag({ channel, compact }: ChannelTagProps) {
  const meta = CHANNEL[channel];
  const { Icon } = meta;

  if (compact) {
    return (
      <Box component="span" title={meta.label} sx={{ color: 'var(--muted)', display: 'inline-flex' }}>
        <Icon sx={{ fontSize: 14 }} />
      </Box>
    );
  }

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        padding: '2px 7px',
        borderRadius: '4px',
        backgroundColor: 'var(--card-sub)',
        border: '1px solid var(--hairline)',
        color: 'var(--muted)',
        fontSize: 11.5,
        fontWeight: 500,
        whiteSpace: 'nowrap'
      }}
    >
      <Icon sx={{ fontSize: 12 }} />
      {meta.label}
    </Box>
  );
}
